import React from 'react';

/**
 * Recursively searches through a React component tree to find the first child
 * component that matches the specified component type.
 *
 * Unlike `React.Children.toArray().find()` which only searches immediate children,
 * this function performs a depth-first search through the entire component tree,
 * allowing it to find components nested at any level.
 *
 * @param children - The React children to search through
 * @param targetType - The React component type to search for
 * @returns The first matching React element, or null if not found
 *
 * @example
 * // Find a deeply nested component
 * const trigger = findChildByType(children, MyComponent.Trigger);
 *
 * // Works with nested structures like:
 * // <MyComponent>
 * //   <div>
 * //     <span>
 * //       <MyComponent.Trigger>Click me</MyComponent.Trigger>
 * //     </span>
 * //   </div>
 * //   <MyComponent.Content>Tooltip content</MyComponent.Content>
 * // </MyComponent>
 */
export function findChildByType(
  children: React.ReactNode,
  targetType: React.ComponentType<any>,
): React.ReactElement | null {
  let found: React.ReactElement | null = null;

  React.Children.forEach(children, (child) => {
    if (found) return; // Early exit if already found

    if (React.isValidElement(child)) {
      if (child.type === targetType) {
        found = child;
      } else if (child.props.children) {
        // Recursively search nested children
        found = findChildByType(child.props.children, targetType);
      }
    }
  });

  return found;
}
